import React, { useCallback, useMemo, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppInput } from '../../components/AppInput';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { ApprovalDetailField } from '../../components/ApprovalDetailField';
import { ScreenHeader } from '../../components/ScreenHeader';
import { ErrorState } from '../../components/ErrorState';
import { LeaveStatusBadge } from '../../components/LeaveStatusBadge';
import { PermissionGuard } from '../../components/PermissionGuard';
import { ProfileAvatar } from '../../components/ProfileAvatar';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import { getLeaveModuleCapabilities } from '../../config/leaveCapabilities';
import type { ColorPalette } from '../../constants/colors';
import { PERMISSIONS } from '../../constants/permissions';
import { type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import {
  cancelLeaveApplication,
  getLeaveApplicationDetails,
  getLeaveSessionTypes,
} from '../../services/leaveService';
import type {
  LeaveApplicationDetails,
  LeaveSessionOption,
} from '../../types/leave';
import { formatDisplayLabel } from '../../utils/camelCaseLabel';
import { formatAppliedOn, formatLeaveDate, formatLeaveDuration } from '../../utils/dateTime';
import { leaveErrorMessage } from '../../utils/leaveErrors';
import {
  formatLeaveSessionLabel,
  leavePortionFieldLabel,
} from '../../utils/leaveSession';
import { isRejectedWorkflowStatus, isCancelledWorkflowStatus } from '../../utils/workflowStatus';

type Props = NativeStackScreenProps<RootStackParamList, 'LeaveDetails'>;

function Field({
  label,
  value,
  palette,
  link = false,
  expandable = false,
}: {
  label: string;
  value: string;
  palette: ColorPalette;
  link?: boolean;
  expandable?: boolean;
}) {
  return (
    <ApprovalDetailField
      label={label}
      value={value}
      palette={palette}
      link={link}
      expandable={expandable}
    />
  );
}

/**
 * Loads full leave application details when the details API is available.
 * Layout matches the leave request approval card (without approve/reject).
 */
export function LeaveApplicationDetailsScreen({ navigation, route }: Props) {
  const auth = useAuth();
  const palette = useAppColors();
  const capabilities = getLeaveModuleCapabilities(auth.permissions);
  const params = route.params;
  const leaveApplicationId = params.leaveApplicationId;

  const [details, setDetails] = useState<LeaveApplicationDetails | null>(null);
  const [sessions, setSessions] = useState<LeaveSessionOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCancelReason, setShowCancelReason] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!Number.isInteger(leaveApplicationId) || leaveApplicationId <= 0) {
      setDetails(null);
      setError('Leave application was not found.');
      setLoading(false);
      return;
    }
    if (!capabilities.canViewDetails) {
      setDetails(null);
      setError('Leave application details are not currently available.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const [result, sessionResult] = await Promise.allSettled([
        getLeaveApplicationDetails(leaveApplicationId),
        getLeaveSessionTypes(),
      ]);
      if (result.status === 'rejected') {
        throw result.reason;
      }
      setDetails(result.value);
      setSessions(
        sessionResult.status === 'fulfilled'
          ? sessionResult.value.flatMap(option => option.sessions)
          : [],
      );
    } catch (caught) {
      setDetails(null);
      setSessions([]);
      setError(
        leaveErrorMessage(caught, 'Unable to load leave application details.'),
      );
    } finally {
      setLoading(false);
    }
  }, [capabilities.canViewDetails, leaveApplicationId]);

  useFocusEffect(
    useCallback(() => {
      load().catch(() => undefined);
    }, [load]),
  );

  const confirmCancel = async () => {
    const trimmedReason = cancelReason.trim();
    if (!trimmedReason) {
      setActionError('Cancellation reason is required.');
      return;
    }
    setCancelling(true);
    setActionError(null);
    try {
      await cancelLeaveApplication(leaveApplicationId, trimmedReason);
      navigation.goBack();
    } catch (caught) {
      setActionError(
        leaveErrorMessage(caught, 'Unable to cancel leave application.'),
      );
    } finally {
      setCancelling(false);
    }
  };

  // Always show the request owner from the leave API — never the active
  // role / logged-in viewer (dual Employee+TL accounts must not swap names).
  const employeeName =
    details?.employee.displayName?.trim() ||
    details?.employee.employeeNumber?.trim() ||
    'Leave application';
  const leaveType = formatDisplayLabel(
    details?.leaveType.leaveTypeName || params.leaveTypeName,
    'Not available',
  );
  const fromDate = formatLeaveDate(
    details?.leaveFromDate ?? params.leaveFromDate,
  );
  const toDate = formatLeaveDate(details?.leaveToDate ?? params.leaveToDate);
  const totalDays = formatLeaveDuration(
    details?.totalDays ?? params.totalDays ?? null,
  );
  const session = formatLeaveSessionLabel(
    details?.sessionType ?? params.sessionType,
    sessions,
    details?.sessionTypeName ?? params.sessionTypeName,
  );
  const reason = details?.reason?.trim() || 'Not available';
  const handoverNotes = details?.handoverNotes?.trim() || 'Not available';
  const appliedOn = formatAppliedOn(details?.submittedAt ?? null);
  const storedRejectReason = details?.rejectReason?.trim();
  const storedCancellationReason = details?.cancellationReason?.trim();
  const attachment = details?.attachmentName?.trim();
  const isRejected = isRejectedWorkflowStatus(details?.statusCode);
  const isCancelled = isCancelledWorkflowStatus(details?.statusCode);

  const themed = useMemo(
    () => ({
      name: { ...typography.sectionTitle, color: palette.textPrimary },
      meta: { ...typography.caption, color: palette.textSecondary },
      actionError: { ...typography.caption, color: palette.error },
    }),
    [palette],
  );

  if (!Number.isInteger(leaveApplicationId) || leaveApplicationId <= 0) {
    return (
      <PermissionGuard
        permission={PERMISSIONS.LEAVE_APPLY}
        anyPermissions={[PERMISSIONS.LEAVE_APPROVE]}
        fallback={<UnauthorizedScreen />}
      >
        <AppScreen contentStyle={styles.center}>
          <ScreenHeader
            title="Leave application"
            onBack={navigation.goBack}
          />
          <ErrorState message="Leave application was not found." />
        </AppScreen>
      </PermissionGuard>
    );
  }

  return (
    <PermissionGuard
      permission={PERMISSIONS.LEAVE_APPLY}
      anyPermissions={[PERMISSIONS.LEAVE_APPROVE]}
      fallback={<UnauthorizedScreen />}
    >
      {loading && !details ? (
        <AppScreen contentStyle={styles.center}>
          <ScreenHeader
            title="Leave application"
            onBack={navigation.goBack}
          />
          <AppLoader message="Loading leave application" />
        </AppScreen>
      ) : error && !details ? (
        <AppScreen contentStyle={styles.center}>
          <ScreenHeader
            title="Leave application"
            onBack={navigation.goBack}
          />
          <ErrorState message={error} onRetry={load} />
        </AppScreen>
      ) : details ? (
      <AppScreen scroll>
        <ScreenHeader title={employeeName} onBack={navigation.goBack} />

        <View style={styles.body}>
        <AppCard style={styles.card}>
          <View style={styles.profileRow}>
            <ProfileAvatar name={employeeName} size={56} />
            <View style={styles.profileText}>
              <Text style={themed.name}>{employeeName}</Text>
              {details.employee.employeeNumber ? (
                <Text style={themed.meta}>
                  {details.employee.employeeNumber}
                </Text>
              ) : null}
              {details.employee.designationName ? (
                <Text style={themed.meta}>
                  {details.employee.designationName}
                </Text>
              ) : null}
            </View>
            <LeaveStatusBadge
              label={details.statusLabel}
              statusCode={details.statusCode}
            />
          </View>

          <Field label="Leave Type" value={leaveType} palette={palette} />
          <Field label="From Date" value={fromDate} palette={palette} />
          <Field label="To Date" value={toDate} palette={palette} />
          <Field label="Total Days" value={totalDays} palette={palette} />
          <Field
            label={leavePortionFieldLabel(
              details.sessionType ?? params.sessionType,
              sessions,
            )}
            value={session}
            palette={palette}
          />
          <Field label="Reason" value={reason} palette={palette} />
          <Field
            label="Handover Notes"
            value={handoverNotes}
            palette={palette}
            expandable
          />
          <Field label="Applied On" value={appliedOn} palette={palette} />
          {isRejected ? (
            <Field
              label="Reject Reason"
              value={storedRejectReason || 'Not available'}
              palette={palette}
              expandable
            />
          ) : null}
          {isCancelled ? (
            <Field
              label="Cancellation Reason"
              value={storedCancellationReason || 'Not available'}
              palette={palette}
              expandable
            />
          ) : null}
          {attachment ? (
            <Field
              label="Attachments"
              value={attachment}
              palette={palette}
              link
            />
          ) : null}
        </AppCard>

        {capabilities.canCancel && details.canCancel ? (
          showCancelReason ? (
            <View style={styles.cancelBlock}>
              <AppInput
                label="Cancellation reason"
                value={cancelReason}
                onChangeText={value => {
                  setCancelReason(value);
                  setActionError(null);
                }}
                placeholder="Enter reason for cancellation"
              />
              {actionError ? (
                <Text accessibilityRole="alert" style={themed.actionError}>
                  {actionError}
                </Text>
              ) : null}
              <AppButton
                title="Confirm Cancel"
                variant="danger"
                onPress={confirmCancel}
                loading={cancelling}
                disabled={cancelling}
              />
              <AppButton
                title="Keep Leave"
                variant="text"
                onPress={() => {
                  setShowCancelReason(false);
                  setCancelReason('');
                  setActionError(null);
                }}
                disabled={cancelling}
              />
            </View>
          ) : (
            <AppButton
              title="Cancel Leave"
              variant="danger"
              onPress={() => setShowCancelReason(true)}
            />
          )
        ) : null}
        </View>
      </AppScreen>
      ) : null}
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  body: { marginTop: spacing.lg },
  center: { justifyContent: 'center', gap: spacing.md },
  card: { gap: 0, marginBottom: spacing.md, paddingBottom: spacing.sm },
  profileRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.md,
    marginBottom: spacing.md,
  },
  profileText: { flex: 1, gap: 2 },
  cancelBlock: { gap: spacing.md, marginBottom: spacing.xl },
});
